<template>
  <v-data-table
    fixed-header
    :class="[`ag-data-table elevation-${elevation}`, { 'ag-data-table--fixed-layout': fixedLayout }]"
    :headers="localHeaders"
    :height="computedHeight"
    :hide-default-footer="hideDefaultFooter"
    :items="items"
    :items-per-page="itemsPerPage"
    :no-data-text="noDataText"
    :page="page"
    :search="search"
    :show-expand="showExpand"
    :single-expand="singleExpand"
    :items-per-page-text="itemsPerPageText"
    :items-per-page-options="itemsPerPageOptions"
  >
    <template #top>
      <v-progress-linear v-show="loading" color="primary" height="4px" indeterminate rounded />
    </template>

    <template #headers="{ columns, isSorted, getSortIcon, toggleSort, sortBy }">
      <tr>
        <th
          v-for="head in columns"
          :key="head.key"
          class="v-data-table__th"
          :class="headClass(head)"
          :data-is-sorted="getSortType(head, sortBy)"
          :data-sortable="head.sortable !== false"
          :style="{ width: head.width, minWidth: head.width }"
          @click="head.sortable !== false ? toggleSort(head) : undefined"
        >
          <div class="ag-data-table__header-container">
            <div>
              <img v-if="head.isRecommendation" height="18" src="/favicon.ico" alt="logo" />
              <div style="line-height: 25px; padding-top: 1px">{{ head.text }}</div>
              <AGTooltip
                v-if="head.largeTooltip"
                top
                large-tooltip
                :large-tooltip-title="head.largeTooltipTitle"
                :large-tooltip-icon="head.largeTooltipIcon"
              >
                <template #largeTooltipContent>
                  <div v-html="head.largeTooltipContent" />
                </template>
                <AGIcon class="ag-data-table__help" :icon="head.helpIcon ?? 'md:help'" />
              </AGTooltip>
              <AGTooltip v-if="head.helpText" top :tooltip-text="head.helpText">
                <AGIcon class="ag-data-table__help" :icon="head.helpIcon ?? 'md:help'" />
              </AGTooltip>
            </div>
            <AGTooltip top :tooltip-text="getOrderTooltip(getSortIcon(head))">
              <v-icon v-if="head.sortable !== false">
                <template v-if="!isSorted(head)">mdi-swap-vertical</template>
                <template v-else>mdi-arrow-down</template>
              </v-icon>
            </AGTooltip>
          </div>
        </th>
      </tr>
    </template>

    <template v-if="loading" #body>
      <tr>
        <td v-for="head in headers" :key="head.value">
          <v-progress-linear color="#ECE7F5" height="25px" style="border-radius: 4px" indeterminate />
        </td>
      </tr>
    </template>

    <template v-for="row in rows" #[`item.${row.value}`]="props">
      <slot :name="`item.${row.value}`" v-bind="props" />
    </template>

    <template v-if="hasExpandSlot" #expanded-row="props">
      <slot name="expanded-item" v-bind="props" />
    </template>

    <template #no-data>
      <div class="ag-data-table__no-data">
        <slot v-if="hasNoDataSlot" name="no-data" />
        {{ noDataText }}
      </div>
    </template>
  </v-data-table>
</template>

<script>
import AGIcon from '@/components/common/AGIcon.vue';
import AGTooltip from '@/components/common/AGTooltip.vue';

export default {
  components: { AGIcon, AGTooltip },
  props: {
    headers: {
      type: Array,
      required: true,
    },
    items: {
      type: Array,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    search: {
      type: String,
      default: undefined,
    },
    page: {
      type: Number,
      default: 1,
    },
    footerProps: {
      type: Object,
      default: () => ({}),
    },
    noDataText: {
      type: String,
      default: 'Não há registros a serem exibidos',
    },
    itemsPerPage: {
      type: Number,
      default: 50,
    },
    itemsPerPageText: {
      type: String,
      default: 'Registros por página:',
    },
    elevation: {
      type: Number,
      default: 0,
    },
    showExpand: {
      type: Boolean,
      default: false,
    },
    singleExpand: {
      type: Boolean,
      default: true,
    },
    height: {
      type: String,
      default: undefined,
    },
    hideDefaultFooter: {
      type: Boolean,
      default: false,
    },
    fixedLayout: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    localHeaders() {
      return this.headers.map((head) => {
        return {
          ...head,
          sortable: head.sortable !== false,
          cellProps: {
            ...head.cellProps,
            sticky: head.value === 'action',
          },
        };
      });
    },
    computedHeight() {
      return this.items.length < 10 && !this.loading ? undefined : this.height;
    },
    hasNoDataSlot() {
      return this.$slots['no-data'] ?? this.$slots['no-data'];
    },
    hasExpandSlot() {
      return this.$slots['expanded-item'] ?? this.$slots['expanded-item'];
    },
    rows() {
      return this.headers.filter((h) => {
        const key = `item.${h.value}`;
        return !!(this.$slots[key] ?? this.$slots[key]);
      });
    },
    itemsPerPageOptions() {
      return [
        { value: 50, title: '50' },
        { value: 100, title: '100' },
        { value: 250, title: '250' },
      ];
    },
  },
  methods: {
    headClass(head) {
      const textAlign = head.align ?? 'left';

      return {
        [`text-${textAlign}`]: true,
        'ag-data-table__sticky-cell': head.value === 'action',
      };
    },

    getOrderTooltip(iconType) {
      return !iconType ? 'Ordenar' : iconType === '$sortAsc' ? 'Crescente' : 'Decrescente';
    },

    getSortType(head, sortBy) {
      const obj = sortBy.find((sort) => sort.key === head.key);

      return obj?.order;
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/styles/table.scss';
</style>
